import React, { createContext, useContext, useReducer, useCallback } from "react";
import { DiscoveryService } from "../services/discovery";
import { DEVICE_TYPES } from "../domain/models";
import { CHANNEL_COUNT, DISCOVERY_DELAY_MS } from "../constants/app";
import {
  telemetryReducer,
  initialTelemetryState,
  createTelemetryActions,
} from "./telemetrySlice";

const AppStateContext = createContext(null);

export const STORAGE_KEY = "cberry_app_state_v1";

function loadPersistedState() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      nodes: parsed.nodes || [],
      devices: parsed.devices || [],
      ui: parsed.ui || { autoCreatePrompts: {} },
      updatedFirmware: parsed.updatedFirmware || {},
      gatewayConnected: parsed.gatewayConnected || false,
      connectedGatewayId: parsed.connectedGatewayId || null,
      connectedGateway: parsed.connectedGateway || null,
    };
  } catch {
    return null;
  }
}

const initialState = (() => {
  const persisted = loadPersistedState();
  return {
    nodes: persisted?.nodes || [],
    devices: persisted?.devices || [],
    discoveryStatus: persisted?.nodes?.length > 0 ? "ready" : "idle",
    lastScanAt: null,
    telemetry: initialTelemetryState,
    ui: persisted?.ui || { autoCreatePrompts: {} },
    updatedFirmware: persisted?.updatedFirmware || {},
    gatewayConnected: persisted?.gatewayConnected || false,
    connectedGatewayId: persisted?.connectedGatewayId || null,
    connectedGateway: persisted?.connectedGateway || null,
  };
})();

export function appReducer(state, action) {
  if (action.type.startsWith("telemetry/")) {
    return { ...state, telemetry: telemetryReducer(state.telemetry, action) };
  }
  switch (action.type) {
    case "start-scan":
      return { ...state, discoveryStatus: "scanning" };

    case "load-discovery":
      return {
        ...state,
        nodes: action.payload.nodes,
        devices: action.payload.devices,
        discoveryStatus: "ready",
        lastScanAt: new Date().toISOString(),
      };

    case "ui/needs-auto-create":
      return {
        ...state,
        ui: {
          ...state.ui,
          autoCreatePrompts: { ...state.ui.autoCreatePrompts, [action.nodeId]: true },
        },
      };

    case "nodes/mark-prompted":
      return {
        ...state,
        nodes: state.nodes.map((n) =>
          n.id === action.nodeId ? { ...n, autoCreatePrompted: true } : n
        ),
        ui: {
          ...state.ui,
          autoCreatePrompts: { ...state.ui.autoCreatePrompts, [action.nodeId]: false },
        },
      };

    case "channels/assign":
      return {
        ...state,
        nodes: state.nodes.map((node) => {
          if (node.id !== action.nodeId) return node;
          const channels = (node.channels || []).map((ch) =>
            ch.index === action.channelIndex ? { ...ch, ownerDeviceId: action.deviceId } : ch
          );
          return { ...node, channels };
        }),
      };

    case "channels/release":
      return {
        ...state,
        nodes: state.nodes.map((node) => {
          if (node.id !== action.nodeId) return node;
          const channels = (node.channels || []).map((ch) =>
            ch.index === action.channelIndex ? { ...ch, ownerDeviceId: null } : ch
          );
          return { ...node, channels };
        }),
      };

    case "devices/addMany":
      return { ...state, devices: [...state.devices, ...(action.payload || [])] };

    case "toggle-device":
      return {
        ...state,
        devices: state.devices.map((d) =>
          d.id === action.id ? { ...d, control: { ...d.control, on: action.on } } : d
        ),
      };

    case "dim-device":
      return {
        ...state,
        devices: state.devices.map((d) =>
          d.id === action.id ? { ...d, control: { ...d.control, dim: action.value } } : d
        ),
      };

    case "update-meta":
      return {
        ...state,
        devices: state.devices.map((d) =>
          d.id === action.id ? { ...d, ...action.patch } : d
        ),
      };

    case "toggle-dashboard":
      return {
        ...state,
        devices: state.devices.map((d) =>
          d.id === action.id ? { ...d, showOnDashboard: action.show } : d
        ),
      };

    case "nodes/firmware-updated":
      return {
        ...state,
        updatedFirmware: {
          ...state.updatedFirmware,
          [action.nodeId]: { version: action.newVersion, updatedAt: new Date().toISOString() },
        },
        nodes: state.nodes.map((n) =>
          n.id === action.nodeId
            ? { ...n, fw: action.newVersion, updateAvailable: false, status: "OK" }
            : n
        ),
      };

    case "nodes/update-meta":
      return {
        ...state,
        nodes: state.nodes.map((n) =>
          n.id === action.nodeId ? { ...n, ...action.patch } : n
        ),
      };

    case "gateway/connected":
      return {
        ...state,
        gatewayConnected: true,
        connectedGatewayId: action.gatewayId ?? state.connectedGatewayId,
        connectedGateway: action.gateway ?? state.connectedGateway,
      };

    case "gateway/update-meta":
      return {
        ...state,
        connectedGateway: state.connectedGateway
          ? { ...state.connectedGateway, ...action.patch }
          : state.connectedGateway,
      };

    case "disconnect":
      return {
        ...state,
        nodes: [],
        devices: [],
        discoveryStatus: "idle",
        lastScanAt: null,
        gatewayConnected: false,
        connectedGatewayId: null,
        connectedGateway: null,
      };

    case "channels/reconcile-mapping": {
      const { deviceId, desiredKeys } = action;
      const busToNodeId = Object.fromEntries(
        state.nodes.map((n) => [n.name || n.id, n.id])
      );
      const desiredSet = new Set(desiredKeys);

      // Release channels this device owns that are no longer desired
      let nodes = state.nodes.map((node) => ({
        ...node,
        channels: (node.channels || []).map((ch) => {
          const key = `${node.name || node.id}::Ch ${ch.index}`;
          if (ch.ownerDeviceId === deviceId && !desiredSet.has(key)) {
            return { ...ch, ownerDeviceId: null };
          }
          return ch;
        }),
      }));

      // Assign newly desired channels (override any existing owner)
      for (const key of desiredKeys) {
        const sepIdx = key.indexOf("::");
        if (sepIdx === -1) continue;
        const bus = key.slice(0, sepIdx);
        const chLabel = key.slice(sepIdx + 2);
        const nodeId = busToNodeId[bus];
        const chIdx = Number(chLabel.replace("Ch ", ""));
        if (!nodeId || Number.isNaN(chIdx)) continue;
        nodes = nodes.map((node) => {
          if (node.id !== nodeId) return node;
          return {
            ...node,
            channels: (node.channels || []).map((ch) =>
              ch.index === chIdx ? { ...ch, ownerDeviceId: deviceId } : ch
            ),
          };
        });
      }

      return { ...state, nodes };
    }

    default:
      return state;
  }
}

export function AppStateProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const telemetryActions = createTelemetryActions(dispatch);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          nodes: state.nodes,
          devices: state.devices,
          ui: state.ui,
          updatedFirmware: state.updatedFirmware,
          gatewayConnected: state.gatewayConnected,
          connectedGatewayId: state.connectedGatewayId,
          connectedGateway: state.connectedGateway,
        })
      );
    } catch {
      // ignore storage errors (private browsing quota, etc.)
    }
  }, [state.nodes, state.devices, state.ui, state.updatedFirmware, state.gatewayConnected, state.connectedGatewayId, state.connectedGateway]);

  const startDiscovery = useCallback(() => {
    dispatch({ type: "start-scan" });
    setTimeout(() => {
      const result = DiscoveryService.discover();
      dispatch({ type: "load-discovery", payload: { nodes: result.nodes, devices: result.devices } });
      // Flag each EWL-810 that has no PDM channels yet so the auto-create prompt fires
      result.nodes
        .filter((n) => n.subtype === "EWL-810")
        .forEach((node) => {
          const hasChannels = result.devices.some(
            (d) => d.type === DEVICE_TYPES.PDM_CHANNEL && d.nodeId === node.id
          );
          if (!hasChannels && !node.autoCreatePrompted) {
            dispatch({ type: "ui/needs-auto-create", nodeId: node.id });
          }
        });
    }, DISCOVERY_DELAY_MS);
  }, []);

  const createPdmChannels = useCallback(
    ({ nodeId, nodeName, count = 1, indices, aggregate = false }) => {
      const node = state.nodes.find((n) => n.id === nodeId);
      const channels =
        node?.channels && node.channels.length > 0
          ? node.channels
          : Array.from({ length: CHANNEL_COUNT }).map((_, idx) => ({
              index: idx + 1,
              ownerDeviceId: null,
              mode: "Dimming",
              resetBehavior: "Defined Value",
              definedValue: 50,
              turnOnDelay: 0,
              turnOffDelay: 0,
              softFuseEnabled: false,
              currentLimit: null,
              overcurrentDuration: null,
              reaction: "Alarm",
              metrics: { current: 0, fuseState: "ok" },
            }));

      const free = channels.filter((ch) => !ch.ownerDeviceId).map((ch) => ch.index);
      let targets = [];
      if (Array.isArray(indices) && indices.length > 0) {
        targets = indices.filter((idx) => free.includes(idx));
      } else {
        targets = free.sort((a, b) => a - b).slice(0, count);
      }
      if (targets.length === 0) return 0;

      if (aggregate) {
        const deviceId = `${nodeId}-agg-${targets.join("-")}`;
        const device = {
          id: deviceId,
          nodeId,
          channelIndex: targets[0],
          type: DEVICE_TYPES.PDM_CHANNEL,
          name: `${nodeName || nodeId} :: ${targets.join(",")}`,
          dimmable: true,
          mode: "Dimming",
          showOnDashboard: false,
          showOnDevices: true,
          status: "ok",
          faults: [],
          metrics: {},
          control: { on: false, dim: 50 },
          softFuseEnabled: false,
          resetBehavior: "Reset",
          turnOnDelay: 0,
          turnOffDelay: 0,
        };
        dispatch({ type: "devices/addMany", payload: [device] });
        targets.forEach((idx) =>
          dispatch({ type: "channels/assign", nodeId, channelIndex: idx, deviceId })
        );
        dispatch({ type: "nodes/mark-prompted", nodeId });
        return 1;
      }

      const list = targets.map((idx) => ({
        id: `${nodeId}-ch${idx}`,
        nodeId,
        channelIndex: idx,
        type: DEVICE_TYPES.PDM_CHANNEL,
        name: `${nodeName || nodeId} :: Ch ${idx}`,
        dimmable: true,
        mode: "Dimming",
        showOnDashboard: false,
        showOnDevices: true,
        status: "ok",
        faults: [],
        metrics: {},
        control: { on: false, dim: 50 },
        softFuseEnabled: false,
        resetBehavior: "Reset",
        turnOnDelay: 0,
        turnOffDelay: 0,
      }));
      dispatch({ type: "devices/addMany", payload: list });
      targets.forEach((idx) =>
        dispatch({ type: "channels/assign", nodeId, channelIndex: idx, deviceId: `${nodeId}-ch${idx}` })
      );
      dispatch({ type: "nodes/mark-prompted", nodeId });
      return list.length;
    },
    [state.nodes]
  );

  const createAggregateDevice = useCallback(
    (slots) => {
      if (!slots || slots.length === 0) return;
      const deviceId = `agg-${slots.map((s) => `${s.nodeId}-${s.index}`).join("_")}`;
      const device = {
        id: deviceId,
        nodeId: slots[0].nodeId,
        channelIndex: slots[0].index,
        type: DEVICE_TYPES.PDM_CHANNEL,
        name: `Aggregate :: ${slots.map((s) => `${s.nodeName || s.nodeId}-${s.index}`).join(",")}`,
        dimmable: true,
        mode: "Dimming",
        showOnDashboard: false,
        showOnDevices: true,
        status: "ok",
        faults: [],
        metrics: {},
        control: { on: false, dim: 50 },
        softFuseEnabled: false,
        resetBehavior: "Reset",
        turnOnDelay: 0,
        turnOffDelay: 0,
      };
      dispatch({ type: "devices/addMany", payload: [device] });
      slots.forEach(({ nodeId, index }) =>
        dispatch({ type: "channels/assign", nodeId, channelIndex: index, deviceId })
      );
    },
    []
  );

  const value = React.useMemo(
    () => ({
      ...state,
      actions: {
        startDiscovery,
        createPdmChannels,
        createAggregateDevice,
        markPrompted: (nodeId) => dispatch({ type: "nodes/mark-prompted", nodeId }),
        reconcileChannelMapping: (deviceId, desiredKeys) =>
          dispatch({ type: "channels/reconcile-mapping", deviceId, desiredKeys }),
        assignChannel: (nodeId, channelIndex, deviceId) =>
          dispatch({ type: "channels/assign", nodeId, channelIndex, deviceId }),
        releaseChannel: (nodeId, channelIndex) =>
          dispatch({ type: "channels/release", nodeId, channelIndex }),
        addDevices: (list) => dispatch({ type: "devices/addMany", payload: list }),
        toggleDevice: (id, on) => dispatch({ type: "toggle-device", id, on }),
        setDim: (id, value) => dispatch({ type: "dim-device", id, value }),
        updateMeta: (id, patch) => dispatch({ type: "update-meta", id, patch }),
        setShowOnDashboard: (id, show) => dispatch({ type: "toggle-dashboard", id, show }),
        markFirmwareUpdated: (nodeId, newVersion) =>
          dispatch({ type: "nodes/firmware-updated", nodeId, newVersion }),
        updateNodeMeta: (nodeId, patch) => dispatch({ type: "nodes/update-meta", nodeId, patch }),
        connectGateway: (gatewayId, gateway) => dispatch({ type: "gateway/connected", gatewayId, gateway }),
        updateGatewayMeta: (patch) => dispatch({ type: "gateway/update-meta", patch }),
        disconnect: () => dispatch({ type: "disconnect" }),
        updateTelemetry: telemetryActions.updateTelemetry,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, startDiscovery, createPdmChannels, createAggregateDevice, telemetryActions.updateTelemetry]
  );

  return (
    <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
  );
}

export function useAppState() {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error("useAppState must be used within AppStateProvider");
  return ctx;
}
